import { getClient } from "app/services/missionStorageApi";
import {
  fetchStcmFilesList,
  fetchStcmMetadata,
  getStcmMapListBaseUrl,
  parseEditedBmpsFromResponse,
  storageMapRowLabel,
} from "lib/consts/stcmMapList";
import { validateAndNormalizeMapBaseName } from "lib/protocols/mapLoad";

const GET_MAPS_FOR_MISSION_BUILDER = `
  query GetMapsForMissionBuilder {
    maps {
      id
      name
    }
  }
`;

const GET_MISSION_NAMES_FOR_MAP_PICKER = `
  query MissionNamesForMapPicker {
    missions {
      name
    }
  }
`;

/** Suffix for on-device map files created via mission “new map” flow. */
export const MISSION_MAP_NAME_SUFFIX = "_mission_map";

const MAP_VARIANT_FILES_QUERY = `
  query MissionStorageBmpVariantMapFiles {
    mapFiles(type: "bmp_variant") {
      id
      mapId
      type
      name
      ext
      filePath
    }
  }
`;

const MAP_VARIANT_FILES_QUERY_NO_FILEPATH = `
  query MissionStorageBmpVariantMapFilesNoPath {
    mapFiles(type: "bmp_variant") {
      id
      mapId
      type
      name
      ext
    }
  }
`;

export type MissionStorageMapRow = { id: string; name: string };

/** Base map plus its edited variants for the Load Map picker (Map Manager–style grouping). */
export type MissionStorageMapPickerGroup = {
  mapId: string;
  base: MissionStorageLoadMapPickerRow;
  variants: MissionStorageLoadMapPickerRow[];
};

/** One selectable row in the mission Load Map dialog (base STCM or edited BMP variant). */
export type MissionStorageLoadMapPickerRow = {
  rowId: string;
  mapId: string;
  label: string;
  /** Relative `.stcm` or raster path for preview URLs */
  previewRelPath: string;
  isVariant: boolean;
  /** Parent `.stcm` path (relative to map root); set for variants so the editor can load the base map. */
  baseStcmRelPath?: string;
  variantBmpRelPath?: string;
  mapFileId?: string;
};

/** Relative `.stcm` path for `MapManagerProvider.loadStcmFromRelativePath` (base row or variant’s parent STCM). */
export function stcmRelativePathForLoadMapPickerRow(row: MissionStorageLoadMapPickerRow): string {
  if (!row.isVariant) {
    const p = normRelPath(row.previewRelPath);
    return p.toLowerCase().endsWith(".stcm") ? p : `${p}.stcm`;
  }
  const base = (row.baseStcmRelPath || "").trim();
  if (!base) {
    throw new Error("Variant map row is missing base STCM path");
  }
  const n = normRelPath(base);
  if (!n.toLowerCase().endsWith(".stcm")) {
    return `${n}.stcm`;
  }
  return n;
}

export async function fetchMissionStorageMaps(
  arcHost?: string | null,
  arcId?: string | null,
): Promise<MissionStorageMapRow[]> {
  const data = await getClient(arcHost, arcId).request<{ maps: MissionStorageMapRow[] }>(
    GET_MAPS_FOR_MISSION_BUILDER
  );
  return Array.isArray(data.maps) ? data.maps : [];
}

/** Resolve a relative on-device `.stcm` path to mission_storage `map_id` (by stem / name). */
export async function missionMapIdForStcmRelPath(
  relativePath: string,
  arcHost?: string | null,
  arcId?: string | null,
): Promise<string | null> {
  const norm = normRelPath(relativePath);
  const leaf = (norm.split("/").pop() ?? norm).replace(/\.stcm$/i, "");
  if (!leaf) return null;
  const maps = await fetchMissionStorageMaps(arcHost, arcId);
  return findMapIdByStcmStem(maps, leaf);
}

/** Resolve mission-storage `map_id` to relative `.stcm` path for filter materialize / overlay session. */
export async function stcmRelPathForMissionMapId(
  mapId: string,
  arcHost?: string | null,
  arcId?: string | null,
): Promise<string | null> {
  const id = mapId.trim();
  if (!id) return null;
  const maps = await fetchMissionStorageMaps(arcHost, arcId);
  const row = maps.find((m) => m.id === id);
  const name = row?.name?.trim();
  if (!name) return null;
  const n = normRelPath(name);
  return n.toLowerCase().endsWith(".stcm") ? n : `${n}.stcm`;
}

async function fetchMissionNamesForMapPicker(
  arcHost?: string | null,
  arcId?: string | null,
): Promise<string[]> {
  try {
    const data = await getClient(arcHost, arcId).request<{ missions?: { name: string }[] }>(
      GET_MISSION_NAMES_FOR_MAP_PICKER,
    );
    return (data.missions || []).map((m) => (m.name || "").trim()).filter(Boolean);
  } catch {
    return [];
  }
}

/** Sanitized mission title stem (no extension, not yet validated). */
export function sanitizeMissionTitleForMapStem(title: string): string {
  const raw = title
    .trim()
    .replace(/\s+/g, "_")
    .replace(/[^a-zA-Z0-9_.-]/g, "")
    .replace(/^[_.-]+/, "");
  if (!raw) {
    throw new Error("Mission name must include a letter or number for the map file name");
  }
  return raw;
}

/** On-device STCM base name when saving a map from the mission “new map” flow. */
export function missionMapStorageBaseName(title: string): string {
  return validateAndNormalizeMapBaseName(
    `${sanitizeMissionTitleForMapStem(title)}${MISSION_MAP_NAME_SUFFIX}`,
  );
}

/** List label for a mission-created map in the Load Map picker. */
export function missionMapPickerDisplayLabel(title: string): string {
  return `${sanitizeMissionTitleForMapStem(title)}${MISSION_MAP_NAME_SUFFIX}`;
}

function mapStorageStem(name: string): string {
  const raw = (name || "").trim();
  if (!raw) return "";
  const leaf = (raw.split("/").pop() ?? raw).replace(/\.stcm$/i, "");
  return leaf.toLowerCase();
}

function resolveBaseMapPickerLabel(
  map: MissionStorageMapRow,
  missionNames: string[],
): string {
  const mapStem = mapStorageStem(map.name);
  if (!mapStem) return "Untitled map";

  for (const missionName of missionNames) {
    try {
      const display = missionMapPickerDisplayLabel(missionName);
      const storageStem = missionMapStorageBaseName(missionName).toLowerCase();
      const legacyStem = validateAndNormalizeMapBaseName(
        sanitizeMissionTitleForMapStem(missionName),
      ).toLowerCase();
      if (
        mapStem === storageStem ||
        mapStem === legacyStem ||
        mapStem === display.toLowerCase()
      ) {
        return display;
      }
    } catch {
      /* skip missions with unusable names */
    }
  }

  const stem = (map.name || "").trim();
  return stem.replace(/\.stcm$/i, "") || "Untitled map";
}

export function groupMissionStorageMapPickerRows(
  rows: MissionStorageLoadMapPickerRow[],
): MissionStorageMapPickerGroup[] {
  const baseRows = rows.filter((r) => !r.isVariant);
  const variantRows = rows.filter((r) => r.isVariant);
  const variantsByMapId = new Map<string, MissionStorageLoadMapPickerRow[]>();

  for (const v of variantRows) {
    const list = variantsByMapId.get(v.mapId) ?? [];
    list.push(v);
    variantsByMapId.set(v.mapId, list);
  }

  return baseRows
    .sort((a, b) => a.label.localeCompare(b.label))
    .map((base) => ({
      mapId: base.mapId,
      base,
      variants: (variantsByMapId.get(base.mapId) ?? []).sort((a, b) =>
        a.label.localeCompare(b.label),
      ),
    }));
}

type RawGqlMapFile = {
  id: string;
  mapId?: string;
  map_id?: string;
  name: string;
  ext: string;
  filePath?: string;
  file_path?: string;
};

function normRelPath(p: string): string {
  return p.replace(/\\/g, "/").trim();
}

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function variantLeafStem(relativePath: string): string {
  const base = (relativePath.split("/").pop() ?? relativePath).trim();
  return base.replace(/\.(bmp|png|stcm)$/i, "");
}

/**
 * True for map-editor edited raster variants. Excludes mission_storage artifacts
 * (UUID map-file rows, mission_map saves, non-raster paths).
 */
export function isEditedBmpVariantForPicker(
  variantRelPath: string,
  parentMapName?: string,
): boolean {
  const norm = normRelPath(variantRelPath);
  if (!norm) return false;
  if (!/\.(bmp|png)$/i.test(norm)) return false;

  const leaf = variantLeafStem(norm);
  if (!leaf || UUID_RE.test(leaf)) return false;
  if (leaf.toLowerCase().endsWith(MISSION_MAP_NAME_SUFFIX)) return false;

  const parentStem = (parentMapName || "").trim().replace(/\.stcm$/i, "");
  if (parentStem && leaf.toLowerCase() === parentStem.toLowerCase()) {
    return false;
  }

  return true;
}

/** Same logical variant from GraphQL vs STCM often differs by directory prefix; dedupe on map + leaf name. */
function variantDedupeKey(mapId: string, variantRelPath: string): string {
  const n = normRelPath(variantRelPath)
    .replace(/^(\.\/)+/, "")
    .toLowerCase();
  const leaf = (n.split("/").pop() || n).toLowerCase();
  return `${mapId}\0${leaf}`;
}

async function mapWithConcurrency<T, R>(items: T[], limit: number, fn: (item: T, index: number) => Promise<R>): Promise<R[]> {
  const results = new Array<R>(items.length);
  let next = 0;
  const worker = async () => {
    for (;;) {
      const i = next++;
      if (i >= items.length) break;
      results[i] = await fn(items[i], i);
    }
  };
  const n = Math.max(1, Math.min(limit, items.length));
  await Promise.all(Array.from({ length: n }, () => worker()));
  return results;
}

/** List label for a variant row: variant stem only; optional remark in parentheses. */
function variantPickerLabel(vLabel: string, remark?: string): string {
  const r = (remark || "").trim();
  if (r && r.toLowerCase() !== vLabel.toLowerCase()) {
    return `${vLabel} (${r})`;
  }
  return vLabel;
}

async function tryFetchBmpVariantMapFilesFromGraphql(
  arcHost?: string | null,
  arcId?: string | null,
): Promise<RawGqlMapFile[]> {
  const run = async (query: string): Promise<RawGqlMapFile[]> => {
    const data = await getClient(arcHost, arcId).request<{ mapFiles?: RawGqlMapFile[] }>(query);
    return Array.isArray(data.mapFiles) ? data.mapFiles : [];
  };
  try {
    return await run(MAP_VARIANT_FILES_QUERY);
  } catch {
    try {
      return await run(MAP_VARIANT_FILES_QUERY_NO_FILEPATH);
    } catch {
      return [];
    }
  }
}

/**
 * Maps from mission_storage plus edited BMP variants (GraphQL `mapFiles` and/or map-provider
 * `/stcm-files` + `/stcm-metadata`), grouped for the Load Map picker.
 */
export async function fetchMissionStorageMapPickerGroups(
  arcHost?: string | null,
  isSecure?: boolean,
  arcId?: string | null,
): Promise<MissionStorageMapPickerGroup[]> {
  const [maps, missionNames] = await Promise.all([
    fetchMissionStorageMaps(arcHost, arcId),
    fetchMissionNamesForMapPicker(arcHost, arcId),
  ]);
  const mapsById = new Map(maps.map((m) => [m.id, m]));
  const variantSeen = new Set<string>();
  const rows: MissionStorageLoadMapPickerRow[] = [];

  const pushVariant = (row: Omit<MissionStorageLoadMapPickerRow, "rowId" | "isVariant"> & { rowId?: string }) => {
    const parent = mapsById.get(row.mapId);
    const parentMapName = (parent?.name || "").trim();
    const vPath = row.variantBmpRelPath
      ? normRelPath(row.variantBmpRelPath)
      : normRelPath(row.previewRelPath);
    if (!isEditedBmpVariantForPicker(vPath, parentMapName)) return;

    const key = vPath ? variantDedupeKey(row.mapId, vPath) : `${row.mapId}\0#file:${row.mapFileId ?? ""}`;
    if (variantSeen.has(key)) return;
    variantSeen.add(key);
    rows.push({
      rowId: row.rowId ?? `variant:${row.mapId}:${vPath || row.mapFileId || rows.length}`,
      mapId: row.mapId,
      label: row.label,
      previewRelPath: vPath,
      isVariant: true,
      baseStcmRelPath: row.baseStcmRelPath ? normRelPath(row.baseStcmRelPath) : undefined,
      variantBmpRelPath: vPath,
      mapFileId: row.mapFileId,
    });
  };

  for (const m of maps) {
    const stem = (m.name || "").trim();
    if (!stem) continue;
    const previewRelPath = stem.toLowerCase().endsWith(".stcm") ? stem : `${stem}.stcm`;
    rows.push({
      rowId: `base:${m.id}`,
      mapId: m.id,
      label: resolveBaseMapPickerLabel(m, missionNames),
      previewRelPath: normRelPath(previewRelPath),
      isVariant: false,
    });
  }

  const gqlVariants = await tryFetchBmpVariantMapFilesFromGraphql(arcHost, arcId);
  for (const f of gqlVariants) {
    const mapId = (f.mapId ?? f.map_id ?? "").trim();
    if (!mapId || !mapsById.has(mapId)) continue;
    const parent = mapsById.get(mapId)!;
    const parentStem = (parent.name || "").trim();
    if (!parentStem) continue;
    const baseStcmRelPath =
      parentStem.toLowerCase().endsWith(".stcm") ? parentStem : `${parentStem}.stcm`;
    const relFromGql = (f.filePath ?? f.file_path ?? "").trim();
    if (!relFromGql) {
      // Rows without a path use map-file UUID as `name` — mission artifacts, not editor variants.
      continue;
    }
    const variantRel = normRelPath(relFromGql);
    if (!isEditedBmpVariantForPicker(variantRel, parentStem)) continue;
    const vLabel = storageMapRowLabel(variantRel);
    pushVariant({
      mapId,
      label: variantPickerLabel(vLabel),
      previewRelPath: variantRel,
      baseStcmRelPath,
      variantBmpRelPath: variantRel,
      mapFileId: f.id,
    });
  }

  const stcmBaseUrl = getStcmMapListBaseUrl(arcHost, isSecure, arcId);
  if (stcmBaseUrl) {
    try {
      const stcmList = await fetchStcmFilesList(stcmBaseUrl);
      const stcmPaths = Array.from(
        new Set(
          (Array.isArray(stcmList.files) ? stcmList.files : [])
            .map((p) => normRelPath(String(p)))
            .filter((p) => p.toLowerCase().endsWith(".stcm")),
        ),
      );

      const metadataResults = await mapWithConcurrency(stcmPaths, 8, async (stcmRel) => {
        const mapId = findMapIdByStcmStem(maps, stcmRel.replace(/\.stcm$/i, ""));
        if (!mapId) return { mapId: null as string | null, stcmRel, meta: null };
        try {
          const meta = await fetchStcmMetadata(stcmBaseUrl, stcmRel);
          return { mapId, stcmRel, meta };
        } catch {
          return { mapId, stcmRel, meta: null };
        }
      });

      for (const { mapId, stcmRel, meta } of metadataResults) {
        if (!mapId || !meta?.edited_bmp_variants?.length) continue;
        const baseStcmRelPath = normRelPath(stcmRel);
        for (const v of meta.edited_bmp_variants) {
          const bmpRel = normRelPath(v.bmp_rel_path || "");
          if (!bmpRel) continue;
          const vLabel = storageMapRowLabel(bmpRel);
          pushVariant({
            mapId,
            label: variantPickerLabel(vLabel, v.remarks),
            previewRelPath: bmpRel,
            baseStcmRelPath,
            variantBmpRelPath: bmpRel,
          });
        }
      }

      for (const item of parseEditedBmpsFromResponse(stcmList.edited_bmps)) {
        const bmpRel = normRelPath(item.path);
        if (!bmpRel) continue;
        const stemGuess = bmpRel.replace(/\.(bmp|png)$/i, "");
        const mapId = findMapIdByStcmStem(maps, stemGuess) || findMapIdByStcmStem(maps, bmpRel);
        if (!mapId) continue;
        if (variantSeen.has(variantDedupeKey(mapId, bmpRel))) continue;
        const parent = mapsById.get(mapId);
        const parentStem = (parent?.name || "").trim();
        if (!parentStem) continue;
        const baseStcmRelPath =
          parentStem.toLowerCase().endsWith(".stcm") ? parentStem : `${parentStem}.stcm`;
        const vLabel = storageMapRowLabel(bmpRel);
        pushVariant({
          mapId,
          label: variantPickerLabel(vLabel),
          previewRelPath: bmpRel,
          baseStcmRelPath,
          variantBmpRelPath: bmpRel,
        });
      }
    } catch {
      /* STCM list optional */
    }
  }

  return groupMissionStorageMapPickerRows(rows);
}

/** Flat list of picker rows (base maps then variants). Prefer {@link fetchMissionStorageMapPickerGroups}. */
export async function fetchMissionStorageMapPickerRows(
  arcHost?: string | null,
  isSecure?: boolean,
  arcId?: string | null,
): Promise<MissionStorageLoadMapPickerRow[]> {
  const groups = await fetchMissionStorageMapPickerGroups(arcHost, isSecure, arcId);
  return groups.flatMap((g) => [g.base, ...g.variants]);
}

/** Match map list row to an STCM base name (no extension), case-insensitive. */
export function findMapIdByStcmStem(
  maps: MissionStorageMapRow[],
  baseName: string
): string | null {
  const stem = baseName.trim().toLowerCase();
  if (!stem) return null;
  for (const m of maps) {
    const raw = (m.name || "").trim();
    if (!raw) continue;
    const rawLower = raw.toLowerCase();
    const withoutExt = raw.replace(/\.stcm$/i, "").toLowerCase();
    const lastSeg = (raw.split("/").pop() ?? raw).replace(/\.stcm$/i, "").toLowerCase();
    const lastSegFull = (raw.split("/").pop() ?? raw).toLowerCase();
    if (
      rawLower === stem ||
      rawLower === `${stem}.stcm` ||
      withoutExt === stem ||
      lastSeg === stem ||
      lastSegFull === `${stem}.stcm`
    ) {
      return m.id;
    }
  }
  return null;
}
